import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause } from "lucide-react";

export default function AudioStream() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [streamActive, setStreamActive] = useState(false);
  
  // Refs for Millicast integration
  const audioRef = useRef(null);
  const millicastViewRef = useRef(null);
  const activeSourcesRef = useRef(new Set());

  // Stream configuration
  const accID = "Nm4Aay";
  const streamName = "l99pjn40";

  // Add stream to audio element
  const addStreamToAudioTag = (stream) => {
    if (audioRef.current) {
      audioRef.current.srcObject = stream;
    }
  };

  // Connect to Millicast stream
  const connectStream = useCallback(async () => {
    try {
      if (!window.millicast) {
        console.error('Millicast SDK not loaded');
        return;
      }

      const options = {
        disableVideo: true,
        disableAudio: false,
        bandwidth: 0,
      };

      const tokenGenerator = () =>
        window.millicast.Director.getSubscriber({
          streamName: streamName,
          streamAccountId: accID,
        });

      millicastViewRef.current = new window.millicast.View(streamName, tokenGenerator);

      // Handle track events
      millicastViewRef.current.on("track", (event) => {
        addStreamToAudioTag(event.streams[0]);
      });

      // Handle broadcast events
      millicastViewRef.current.on("broadcastEvent", (event) => {
        const { name, data } = event;
        switch (name) {
          case "active":
            activeSourcesRef.current.add(data.sourceId);
            setStreamActive(true);
            break;
          case "inactive":
            activeSourcesRef.current.delete(data.sourceId);
            if (activeSourcesRef.current.size === 0) {
              setStreamActive(false);
              setIsPlaying(false);
            }
            break;
          default:
            break;
        }
      });

      await millicastViewRef.current.connect(options);
    } catch (e) {
      console.log("Connection failed", e);
      setStreamActive(false);
    }
  }, []);

  // Initialize stream connection
  useEffect(() => {
    connectStream();

    return () => {
      if (millicastViewRef.current) {
        millicastViewRef.current.stop();
      }
    };
  }, [connectStream]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-gray-100 p-6">
      <h1 className="text-2xl font-bold mb-4">Jugendstunde Audiostream</h1>
      <div className="flex items-center mb-6">
        <span className="relative flex h-3 w-3 mr-2">
          {streamActive && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
          )}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${streamActive ? 'bg-red-600' : 'bg-gray-400'}`}></span>
        </span>
        <span className={`font-semibold ${streamActive ? 'text-red-600' : 'text-gray-600'}`}>
          {streamActive ? 'LIVE' : 'OFFLINE'}
        </span>
      </div>
      <button
        onClick={togglePlay}
        disabled={!streamActive}
        className={`flex items-center gap-2 px-6 py-3 text-white rounded-2xl shadow transition ${
          streamActive 
            ? 'bg-blue-600 hover:bg-blue-700' 
            : 'bg-gray-400 cursor-not-allowed'
        }`}
      >
        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        {isPlaying ? "Pause" : "Play"}
      </button>
      
      {/* Hidden audio element for Millicast stream */}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </div>
  );
}